/**
 * TaskIORequestFields
 *
 * Campaign Setup details for the "IO Request & Campaign Setup" request type. Every field is
 * OPTIONAL. The layout follows the `dealType` derived in Task Details (via Form.useWatch):
 *  - CAMPAIGN SETUP DETAILS (shared): Start Date & End Date, Number of Months, Website URL,
 *    Clickthrough URL.
 *  - CHANNEL_PARTNER -> "CHANNEL PARTNER DETAILS" (CPM + Total Budget) then "ADVERTISER DETAILS"
 *    (CPM only).
 *  - DIRECT -> a single "DETAILS" section (CPM + Total Budget).
 *  - Note (shared): freeform special instructions. The Note REPLACES the Task
 *    Description for this type (registry `hideDescription`), so the container hands
 *    the file/image attachment UI in as `attachmentsSlot`, rendered under the Note.
 * The Channel Partner / Advertiser picker itself lives in Task Details.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Input, InputNumber, DatePicker, Divider, Mentions } from 'antd';
import Text from 'antd/lib/typography/Text';

import { useTaskContext } from '../../../../../../core/components/tasks/useTaskContext';
import { currencyProps, urlValidator } from './fieldHelpers';
import '../../../styles/index.scss';

const { RangePicker } = DatePicker;

const NOTE_PLACEHOLDER =
  'Provide special instructions here (i.e. Budget for first month should be higher compared to following months)';

// Whole months covered by the (inclusive) flight range. The end day is counted as covered, so a
// month-aligned flight lands on an exact integer — e.g. Apr 1–Apr 30 = 1, Apr 1–Jun 30 = 3,
// Apr 1–Jun 1 = 2 — and non-aligned ranges round to the nearest whole month. Returns undefined
// until both ends are picked. `start`/`end` are moment instances from the RangePicker.
export const monthsFromFlightRange = (range) => {
  const start = range && range[0];
  const end = range && range[1];
  if (!start || !end) return undefined;
  const months = end.clone().add(1, 'day').diff(start, 'months', true);
  return Math.max(0, Math.round(months));
};

const TaskIORequestFields = ({ form, attachmentsSlot }) => {
  const dealType = Form.useWatch('dealType', form);
  const isCP = dealType === 'CHANNEL_PARTNER';

  // Users available for @mentions in the Note (sourced from TaskProvider).
  const { mentionUsers } = useTaskContext();
  const [mentionSearch, setMentionSearch] = useState('');

  const renderMentionOptions = () =>
    (mentionUsers || [])
      .filter((u) => {
        if (!u.username) return false;
        if (!mentionSearch) return true;
        const q = mentionSearch.toLowerCase();
        return (
          (u.name && u.name.toLowerCase().includes(q)) || u.username.toLowerCase().includes(q)
        );
      })
      .slice(0, 20)
      .map((u) => (
        <Mentions.Option key={u.id} value={u.username}>
          {u.name ? `${u.name} (@${u.username})` : `@${u.username}`}
        </Mentions.Option>
      ));

  // Number of Months is derived from the flight range and shown read-only. Recompute whenever the
  // dates change (covers both create and edit/populate) so the stored value is always correct.
  const flightDates = Form.useWatch('flightDates', form);
  useEffect(() => {
    form.setFieldsValue({ numberOfMonths: monthsFromFlightRange(flightDates) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightDates]);

  return (
    <Col className="task-io-fields">
      <Card
        className="task-assignment-fields__card"
        title={<Text className="task-assignment-fields__card-title">CAMPAIGN SETUP DETAILS</Text>}
      >
        {/* Shared: dates + number of months */}
        <Row gutter={32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item name="flightDates" label="Start Date & End Date" colon={false}>
              <RangePicker className="custom-date task-w--full" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item name="numberOfMonths" label="Number of Months" colon={false}>
              <InputNumber
                readOnly
                min={0}
                placeholder="Auto-calculated from dates"
                className="custom-input task-w--full"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Shared: website + clickthrough URL */}
        <Row gutter={32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="website"
              label="Website URL"
              colon={false}
              rules={[{ validator: urlValidator }]}
            >
              <Input placeholder="https://example.com" className="custom-input" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="clickthroughUrl"
              label="Clickthrough URL"
              colon={false}
              rules={[{ validator: urlValidator }]}
            >
              <Input placeholder="https://example.com/landing" className="custom-input" />
            </Form.Item>
          </Col>
        </Row>

        {/* Money — shape follows the Channel Partner / Advertiser pick */}
        {isCP ? (
          <>
            <Divider orientation="left" className="task-io-fields__section-title">
              CHANNEL PARTNER DETAILS
            </Divider>
            <Row gutter={32}>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item name="partnerCpm" label="CPM" colon={false}>
                  <InputNumber {...currencyProps} placeholder="CPM" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item name="partnerTotalBudget" label="Total Budget" colon={false}>
                  <InputNumber {...currencyProps} placeholder="Total budget" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" className="task-io-fields__section-title">
              ADVERTISER DETAILS
            </Divider>
            <Row gutter={32}>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item name="advertiserCpm" label="CPM" colon={false}>
                  <InputNumber {...currencyProps} placeholder="CPM" />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Divider orientation="left" className="task-io-fields__section-title">
              DETAILS
            </Divider>
            <Row gutter={32}>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item name="cpm" label="CPM" colon={false}>
                  <InputNumber {...currencyProps} placeholder="CPM" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item name="totalBudget" label="Total Budget" colon={false}>
                  <InputNumber {...currencyProps} placeholder="Total budget" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Shared: freeform note / special instructions (replaces the description) */}
        <Row gutter={32}>
          <Col xs={24}>
            <Form.Item name="note" label="Note" colon={false}>
              <Mentions
                autoSize={{ minRows: 4, maxRows: 10 }}
                onSearch={(text) => setMentionSearch(text)}
                filterOption={false}
                placeholder={NOTE_PLACEHOLDER}
                className="custom-textarea"
              >
                {renderMentionOptions()}
              </Mentions>
            </Form.Item>
          </Col>
        </Row>

        {/* File/image attachments, provided by the form container */}
        {attachmentsSlot}
      </Card>
    </Col>
  );
};

TaskIORequestFields.propTypes = {
  form: PropTypes.object.isRequired,
  attachmentsSlot: PropTypes.node,
};

TaskIORequestFields.defaultProps = {
  attachmentsSlot: null,
};

export default TaskIORequestFields;
